import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Image from "@/routing/Image";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { FadeUp } from "../../FadeUp";

// ---------------------------------------------------------------------------
// Static canonical study cards. Kept inline to avoid an API dependency in the
// onboarding flow.
// ---------------------------------------------------------------------------

interface StudyType {
  id: string;
  illustration: { light: string; dark: string };
}

const CANONICAL_STUDY_CARDS: Array<StudyType> = [
  {
    id: "concept-testing",
    illustration: {
      light: "/illustrations/study-illustration-concept-testing.svg",
      dark: "/illustrations/study-illustration-concept-testing-dark.svg",
    },
  },
  {
    id: "brand-perception",
    illustration: {
      light: "/illustrations/study-illustration-brand-perception.svg",
      dark: "/illustrations/study-illustration-brand-perception-dark.svg",
    },
  },
  {
    id: "customer-discovery",
    illustration: {
      light: "/illustrations/study-illustration-customer-discovery.svg",
      dark: "/illustrations/study-illustration-customer-discovery-dark.png",
    },
  },
  {
    id: "ad-messaging",
    illustration: {
      light: "/illustrations/study-illustration-ad-messaging.svg",
      dark: "/illustrations/study-illustration-ad-messaging-dark.svg",
    },
  },
  {
    id: "pricing-packaging",
    illustration: {
      light: "/illustrations/study-illustration-pricing-packaging.svg",
      dark: "/illustrations/study-illustration-pricing-packaging-dark.png",
    },
  },
];

const VISIBLE_COUNT = 3;
const ROTATION_INTERVAL_MS = 4000;

// ---------------------------------------------------------------------------
// StudyCardDisplay — non-interactive version of the wizard StudyTypeCard.
// Same visual (category pill, title, description, illustration) rendered as
// a <div> instead of a <button>.
// ---------------------------------------------------------------------------

function StudyCardDisplay({ studyType }: { studyType: StudyType }) {
  const { t } = useTranslation("onboarding");
  const title = t(`guide.studyCards.${studyType.id}.title`);
  const description = t(`guide.studyCards.${studyType.id}.description`);
  const category = t(`guide.studyCards.${studyType.id}.category`);
  const alt = t("guide.studyCardAlt", { title });
  return (
    <div
      className={cn(
        "relative isolate flex min-h-[300px] w-full flex-col overflow-hidden rounded-[6px] text-left",
        // --onboarding-study-card-bg: light=#F1F1F1 (May 29), dark=alias card-inset (#0B2D2E)
        "bg-[hsl(var(--onboarding-study-card-bg))]",
      )}
    >
      <div className="relative flex flex-col gap-2 px-[15px] pt-[17px] pb-0 flex-1">
        <span
          className={cn(
            "inline-flex w-fit items-center px-3 py-[3px] rounded-full text-[12px] font-medium leading-[1.4]",
            // pill: --onboarding-study-pill-* tokens replace hardcoded #0A2120 / rgba
            "bg-[hsl(var(--onboarding-study-pill-bg))] text-[hsl(var(--onboarding-study-pill-fg))]",
            "dark:border-[1.5px] dark:border-[hsl(var(--onboarding-study-pill-border))]",
          )}
        >
          {category}
        </span>

        <h4 className="text-[18px] font-display leading-none text-black dark:text-white">
          {title}
        </h4>
        {/* --onboarding-study-card-description: aliases --muted-foreground in light;
            pinned to #BDBDBD in dark where --muted-foreground drifted to #8F9999 */}
        <p className="text-[14px] leading-[1.5] text-[hsl(var(--onboarding-study-card-description))]">
          {description}
        </p>
      </div>

      <div className="relative mt-auto h-[147px] overflow-hidden">
        <Image
          src={studyType.illustration.light}
          alt={alt}
          fill
          className="object-contain object-bottom dark:hidden"
          sizes="(max-width: 640px) 100vw, 33vw"
        />
        <Image
          src={studyType.illustration.dark}
          alt={alt}
          fill
          className="object-contain object-bottom hidden dark:block"
          sizes="(max-width: 640px) 100vw, 33vw"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carousel hook — manages a sliding window of `VISIBLE_COUNT` items over the
// canonical study cards, starting from an optional initial position.
// Rotates every `ROTATION_INTERVAL_MS` when not disabled.
// ---------------------------------------------------------------------------

function useCarouselIndex(itemCount: number, disabled: boolean, initialIndex: number = 0) {
  const [startIndex, setStartIndex] = useState(initialIndex);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (disabled || itemCount <= VISIBLE_COUNT) return;
    clear();
    intervalRef.current = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % itemCount);
    }, ROTATION_INTERVAL_MS);
    return clear;
  }, [itemCount, disabled, clear]);

  return startIndex;
}

/** Derive the visible window with wrap-around */
function getVisibleStudyTypes(startIndex: number): Array<StudyType> {
  const count = CANONICAL_STUDY_CARDS.length;
  const result: Array<StudyType> = [];
  for (let i = 0; i < VISIBLE_COUNT; i++) {
    result.push(CANONICAL_STUDY_CARDS[(startIndex + i) % count]);
  }
  return result;
}

// ---------------------------------------------------------------------------
// StudiesSection — section 3 of the Getting Started Guide.
//
// Renders 3 study-type cards at a time in a horizontal carousel that
// auto-rotates. Uses the same card visual as the wizard's StudyTypeCard.
// ---------------------------------------------------------------------------

interface StudiesSectionProps {
  className?: string;
  /**
   * Zero-based index to start the carousel from.
   * Computed from JTBD selections by the parent; defaults to 0.
   */
  initialIndex?: number;
}

const SLIDE_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const MOTION_DURATION = 0.5;

export function StudiesSection({ className, initialIndex = 0 }: StudiesSectionProps) {
  const { t } = useTranslation("onboarding");
  const shouldReduceMotion = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const startIndex = useCarouselIndex(
    CANONICAL_STUDY_CARDS.length,
    !!shouldReduceMotion || paused,
    initialIndex,
  );
  const availableStudyTypes = getVisibleStudyTypes(startIndex);

  // Track whether the entrance animation has completed so subsequent
  // carousel rotations use the horizontal slide instead of the fade-up.
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    // Allow enough time for the staggered entrance to finish before switching
    // to carousel-slide mode (3 cards × 0.15s stagger + 0.5s duration ≈ 1s).
    const timer = setTimeout(() => setInitialized(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Stable indices for stagger delays during the entrance phase.
  const entranceIndices = useMemo(
    () => Object.fromEntries(availableStudyTypes.map((s, i) => [s.id, i])),
    // Only computed once on first render — intentionally no deps update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className={cn("flex flex-col items-center gap-6", className)}>
      <FadeUp delay={0} className="flex flex-col gap-2 text-center">
        <Heading level={2} size="3xl">
          {t("guide.studiesTitle")}
        </Heading>
        <Text color="muted" className="max-w-xl">
          {t("guide.studiesBody")}
        </Text>
      </FadeUp>

      <div className="w-full">
        <div
          className="relative grid w-full grid-cols-3 gap-3 overflow-hidden"
          aria-label={t("guide.studiesAria")}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {availableStudyTypes.map((studyType) => {
              const entranceIdx = entranceIndices[studyType.id] ?? 0;
              const isEntrance = !initialized;

              return (
                <motion.div
                  key={studyType.id}
                  layout
                  initial={
                    shouldReduceMotion
                      ? false
                      : isEntrance
                        ? { opacity: 0, y: 50, scale: 0.8 }
                        : { opacity: 0, x: 80 }
                  }
                  animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
                  exit={
                    shouldReduceMotion
                      ? undefined
                      : isEntrance
                        ? { opacity: 0, scale: 0.8 }
                        : { opacity: 0, x: -80 }
                  }
                  transition={
                    isEntrance
                      ? {
                          delay: shouldReduceMotion ? 0 : 0.3 + entranceIdx * 0.15,
                          duration: shouldReduceMotion ? 0 : MOTION_DURATION,
                          ease: "easeOut",
                        }
                      : {
                          duration: shouldReduceMotion ? 0 : 0.45,
                          ease: SLIDE_EASE,
                        }
                  }
                >
                  <StudyCardDisplay studyType={studyType} />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
